import { useState, useMemo } from "react";
import { Icon } from "./Icon";
import { goToLookup } from "./goToLookup";
import { getRecentVehicleForMake } from "../../utils/recentRegs";
import { slugifyMake } from "../../../lib/vehicle/makes";

const BRANDS = [
  { name: "Audi", file: "imgi_814_audi.svg", group: "german", popular: true },
  { name: "BMW", file: "imgi_818_bmw.svg", group: "german", popular: true },
  { name: "Mercedes-Benz", file: "imgi_840_mercedes.svg", group: "german", popular: true },
  { name: "Volkswagen", file: "imgi_860_volkswagen.svg", group: "german", popular: true },
  { name: "Ford", file: "imgi_826_ford.svg", group: "popular", popular: true },
  { name: "Land Rover", file: "imgi_835_landrover.svg", group: "british", popular: true },
  { name: "Vauxhall", file: "imgi_859_vauxhall.svg", group: "british", popular: true },
  { name: "Toyota", file: "imgi_858_toyota.svg", group: "asian", popular: true },
  { name: "Nissan", file: "imgi_844_nissan.svg", group: "asian", popular: true },
  { name: "Porsche", file: "imgi_878_porsche.svg", group: "german", popular: true },
  { name: "Jaguar", file: "imgi_832_jaguar.svg", group: "british", popular: true },
  { name: "Volvo", file: "imgi_861_volvo.svg", group: "popular", popular: true },
  { name: "MINI", file: "imgi_842_mini.svg", group: "british", popular: true },
  { name: "Hyundai", file: "imgi_830_hyundai.svg", group: "asian", popular: true },
  { name: "Kia", file: "imgi_834_kia.svg", group: "asian", popular: true },
  { name: "Peugeot", file: "imgi_847_peugeot.svg", group: "popular", popular: true },
  { name: "Renault", file: "imgi_850_renault.svg", group: "popular", popular: true },
  { name: "Škoda", file: "imgi_853_skoda.svg", group: "popular", popular: true },
  { name: "SEAT", file: "imgi_852_seat.svg", group: "popular", popular: true },
  { name: "Honda", file: "imgi_827_honda.svg", group: "asian", popular: true },
  { name: "Tesla", file: "imgi_857_tesla.svg", group: "popular", popular: false },
  { name: "Aston Martin", file: "imgi_813_astonmartin.svg", group: "british", popular: false },
  { name: "Bentley", file: "imgi_851_bentley.svg", group: "british", popular: false },
  { name: "Alfa Romeo", file: "imgi_817_alfaromeo.svg", group: "popular", popular: false },
  { name: "Lexus", file: "imgi_836_lexus.svg", group: "asian", popular: false },
  { name: "Mazda", file: "imgi_839_mazda.svg", group: "asian", popular: false },
  { name: "Fiat", file: "imgi_825_fiat.svg", group: "popular", popular: false },
  { name: "DS", file: "imgi_824_ds.svg", group: "popular", popular: false },
  { name: "MG", file: "imgi_841_mg.svg", group: "british", popular: false },
  { name: "Mitsubishi", file: "imgi_843_mitsubishi.svg", group: "asian", popular: false },
  { name: "Subaru", file: "imgi_856_subaru.svg", group: "asian", popular: false },
  { name: "Suzuki", file: "imgi_855_suzuki.svg", group: "asian", popular: false },
  { name: "smart", file: "imgi_854_smart.svg", group: "german", popular: false },
  { name: "Isuzu", file: "imgi_865_isuzu.svg", group: "asian", popular: false },
  { name: "Opel", file: "imgi_846_opel.svg", group: "popular", popular: false },
  { name: "Pontiac", file: "imgi_848_pontiac.svg", group: "popular", popular: false },
];

const FILTERS = [
  { id: "all", label: "All makes" },
  { id: "popular", label: "Popular" },
  { id: "german", label: "German" },
  { id: "british", label: "British" },
  { id: "asian", label: "Asian" },
];

export default function SlBrandsCovered() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return BRANDS.filter((b) => {
      const matchesQuery = !q || b.name.toLowerCase().includes(q);
      if (!matchesQuery) return false;
      if (activeFilter === "all") return true;
      if (activeFilter === "popular") return b.popular;
      return b.group === activeFilter;
    });
  }, [activeFilter, query]);

  return (
    <section className="relative z-10 mx-auto max-w-6xl scroll-mt-24 px-4 py-6 sm:px-6" id="covered-brands">
      <div className="sl-brands-wrap rounded-3xl border border-border bg-card p-4 sm:p-6 md:p-8 shadow-[0_24px_50px_-28px_hsl(var(--primary)/0.2)]">
        <span className="sl-brands-ear">SUPPORTED MAKES</span>

        {/* Header Block */}
        <div className="flex flex-col gap-4 border-b border-border/70 pb-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="font-heading text-xs font-medium uppercase tracking-[0.22em] text-primary">
              Manufacturer Coverage
            </p>
            <h2 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
              Major manufacturers covered from 2012.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Official digital service records are available for major makes from model year 2012 onward. Full risk checks, MOT history and write-off markers cover all UK vehicles.
            </p>
          </div>

          {/* Quick Badges */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 font-medium text-emerald-700">
              <Icon name="check" size={13} className="text-emerald-600" />
              From 2012 model year
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 font-medium text-primary">
              <Icon name="history" size={13} />
              Main dealer trail
            </span>
          </div>
        </div>

        {/* Interactive Search & Filter Controls */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center gap-1.5">
            {FILTERS.map((f) => {
              const isActive = activeFilter === f.id && !query;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setActiveFilter(f.id);
                    setQuery("");
                  }}
                  className={`sl-brand-filter-pill ${isActive ? "is-active" : ""}`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Quick Search Field */}
          <div className="relative w-full sm:w-64">
            <Icon
              name="search"
              size={15}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by make..."
              className="sl-brand-search-input"
              aria-label="Filter supported manufacturers"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear filter"
              >
                <Icon name="x" size={14} />
              </button>
            ) : null}
          </div>
        </div>

        {/* Active Results Counter */}
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {query ? `Showing makes matching "${query}"` : "Click any make to check your car"}
          </span>
          <span className="hidden sm:inline">Tap make to check</span>
        </div>

        {/* Brands Grid */}
        {filtered.length > 0 ? (
          <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4 sm:gap-2.5 md:grid-cols-6 lg:grid-cols-9">
            {filtered.map((item) => {
              const makeSlug = slugifyMake(item.name);
              let recent = null;
              try {
                recent = getRecentVehicleForMake("servicelad", makeSlug);
              } catch {
                recent = null;
              }
              const targetUrl = recent && recent.modelSlug
                ? `/makes/${makeSlug}/${recent.modelSlug}`
                : `/makes/${makeSlug}`;
              const badgeText = recent && recent.model
                ? recent.model
                : "2012+";
              const isSaved = Boolean(recent && recent.model);

              return (
                <a
                  key={item.name}
                  href={targetUrl}
                  className="sl-brand-tile"
                  aria-label={`Check ${item.name} registration`}
                  title={isSaved ? `Check ${item.name} (recent: ${badgeText})` : `Check ${item.name} on ServiceLad`}
                >
                  <div className="sl-brand-logo-frame">
                    <img
                      src={`/brands/${item.file}`}
                      alt={item.name}
                      width={32}
                      height={32}
                      loading="lazy"
                      decoding="async"
                      className="sl-brand-logo"
                    />
                  </div>
                  <span className="sl-brand-name">{item.name}</span>
                  <span className={`sl-brand-year ${isSaved ? "font-semibold text-primary" : ""}`}>
                    {badgeText}
                  </span>
                </a>
              );
            })}
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-dashed border-border p-8 text-center">
            <p className="font-heading text-sm font-semibold text-foreground">
              No service-history match for "{query}"
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Vehicles outside covered makes still get full risk, write-off, finance, stolen and MOT checks.
            </p>
            <button
              type="button"
              onClick={goToLookup}
              className="sl-price-buy mt-4"
            >
              Check your vehicle anyway
            </button>
          </div>
        )}

        {/* Bottom Assurance Card */}
        <div className="mt-7 flex flex-col items-start justify-between gap-4 rounded-2xl border border-border/80 bg-background/80 p-4 sm:flex-row sm:items-center sm:p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon name="shield-check" size={18} />
            </div>
            <div>
              <p className="font-heading text-sm font-semibold text-foreground">
                Not sure if your car has digital records?
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Enter your registration above. We verify manufacturer coverage before taking any payment.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={goToLookup}
            className="sl-price-buy shrink-0 self-stretch sm:self-auto text-center"
          >
            Check your registration
          </button>
        </div>
      </div>
    </section>
  );
}
